
import { Inject, Injectable, DOCUMENT } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { EMPTY, Observable, catchError, exhaustMap, filter, first, map, mergeMap, of, switchMap, tap, timeout, timer, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { routerNavigatedAction } from "@ngrx/router-store";
import { RootState, appActions, fromApp, fromRouter } from "..";
import localeEn from "@angular/common/locales/en";
import localeEs from "@angular/common/locales/es";
import localeDe from "@angular/common/locales/de";
import localeIt from "@angular/common/locales/it";
import localeFr from "@angular/common/locales/fr";
import { registerLocaleData } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { AppRouteParams, AppRoutes, Languages } from "../../enums";
import { Router } from "@angular/router";
import { IntercomService } from "../../services/intercom/intercom.service";
import {
  SERVER_HEALTH_CHECK_TIMEOUT_MS,
  SERVER_RECOVERY_RELOAD_COOLDOWN_MS,
  SERVER_RECOVERY_RELOAD_KEY,
  SERVER_RECOVERY_RELOADS_PER_COOLDOWN,
} from "../../constants";
import { getSafeStorage } from "../../utils/safe-storage/safe-storage";
import { HealthService } from "../../services/api";

@Injectable()
export class AppEffects {
  public changeLanguage$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.changeLanguage),
      withLatestFrom(this.store.select(fromApp.selectLanguage)),
      mergeMap(([action, language]) => {
        const shouldReload = language !== action.language;

        switch (action.language) {
          case Languages.en:
            registerLocaleData(localeEn);
            break;
          case Languages.es:
            registerLocaleData(localeEs);
            break;
          case Languages.de:
            registerLocaleData(localeDe);
            break;
          case Languages.it:
            registerLocaleData(localeIt);
            break;
          case Languages.fr:
            registerLocaleData(localeFr);
            break;
        }

        this.translateService.use(action.language);

        if (shouldReload) this.document.location.reload();

        return [appActions.changeLanguageSuccess({ language: action.language })];
      }),
    ),
  );

  // One failed request (a 5xx, an aborted call) is not an outage: the notice shows only when the API's health check fails too
  public checkServerHealth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.apiServerError),
      withLatestFrom(this.store.select(fromApp.selectServerDown)),
      filter(([, serverDown]) => !serverDown),
      exhaustMap(() =>
        this.healthService.healthControllerCheck().pipe(
          timeout(SERVER_HEALTH_CHECK_TIMEOUT_MS),
          mergeMap(() => EMPTY),
          catchError(() => of(appActions.setServerDown({ serverDown: true }))),
        ),
      ),
    ),
  );

  // Reloading re-runs the initializer and every page load that failed while the API was down, as soon as /health
  // answers. The notice stays until then. An app whose loads keep failing while /health answers would reload on every
  // check of the notice: after two reloads within a minute the next waits for that minute to end (the notice's next
  // checks restart the wait). A retry by hand always reloads right away.
  public reloadAfterOutage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.serverRecovered),
        switchMap(({ manual }) => {
          const storage = getSafeStorage("sessionStorage", this.document.defaultView);
          const recentReloads = this.readRecentRecoveryReloads(storage);
          const wait =
            manual || recentReloads.length < SERVER_RECOVERY_RELOADS_PER_COOLDOWN
              ? 0
              : recentReloads[recentReloads.length - SERVER_RECOVERY_RELOADS_PER_COOLDOWN] + SERVER_RECOVERY_RELOAD_COOLDOWN_MS - Date.now();
          return (wait > 0 ? timer(wait) : of(null)).pipe(
            tap(() => {
              const reloads = [...this.readRecentRecoveryReloads(storage), Date.now()];
              storage.setItem(SERVER_RECOVERY_RELOAD_KEY, JSON.stringify(reloads));
              this.document.location.reload();
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  public logOut$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.logOut),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      first(([, slug]) => !!slug),
      tap(([, slug]) => {
        this.router.navigate([AppRoutes.root, slug, AppRoutes.login]);
        if (window.posthog) window.posthog.reset();
        this.intercomService.shutdown();
      }),
      map(() => appActions.logOutSuccess()),
    ),
  );

  public persistLastRoute$: Observable<ReturnType<typeof appActions.setLastRoute>> = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      map(action => action.payload.routerState.url),
      filter(url => {
        const [path] = (url ?? "").split(/[?#]/);
        const segments = path.split("/").filter(Boolean);
        return segments.length > 0 && !segments.includes(AppRoutes.login) && !segments.includes(AppRoutes.notFound) && !segments.includes(AppRoutes.onboarding);
      }),
      map(url => appActions.setLastRoute({ url })),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<RootState>,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly intercomService: IntercomService,
    private readonly healthService: HealthService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  // Times of the recovery reloads within the cooldown; an older release stored a single time
  private readRecentRecoveryReloads(storage: Storage): number[] {
    let stored: unknown;
    try {
      stored = JSON.parse(storage.getItem(SERVER_RECOVERY_RELOAD_KEY) ?? "[]");
    } catch {
      stored = [];
    }
    const times = (Array.isArray(stored) ? stored : [stored]).filter((time): time is number => typeof time === "number");
    return times.filter(time => Date.now() - time < SERVER_RECOVERY_RELOAD_COOLDOWN_MS);
  }
}
